import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider, useTheme, Topbar, SectionCard, LoadingScreen, EmptyState, StatusBadge } from '../../shared/DashWidgets';
import Sidebar from '../../shared/Sidebar';
import { buildStyles, COLORS } from '../../shared/DashTheme';
import api from '../../../services/api';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function RateRing({ rate, size }) {
  const sz = size || 54; const r = sz / 2 - 5; const circ = 2 * Math.PI * r;
  const dash = (Math.min(rate, 100) / 100) * circ;
  const clr = rate >= 70 ? COLORS.green : rate >= 40 ? COLORS.amber : COLORS.red;
  return (
    <svg width={sz} height={sz} style={{ flexShrink: 0 }}>
      <circle cx={sz/2} cy={sz/2} r={r} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth={5} />
      <circle cx={sz/2} cy={sz/2} r={r} fill="none" stroke={clr} strokeWidth={5}
        strokeDasharray={dash + ' ' + circ} strokeLinecap="round"
        transform={'rotate(-90 ' + (sz/2) + ' ' + (sz/2) + ')'} />
      <text x={sz/2} y={sz/2+4} textAnchor="middle" fill="#fff" fontSize={10} fontWeight={700}>{rate}%</text>
    </svg>
  );
}

function StatusBar({ total, pending, verified, resolved, rejected, escalated }) {
  const safe = total || 1;
  const segs = [
    { label: 'Pending',   count: pending   || 0, color: COLORS.amber  },
    { label: 'Verified',  count: verified  || 0, color: COLORS.blue   },
    { label: 'Resolved',  count: resolved  || 0, color: COLORS.green  },
    { label: 'Rejected',  count: rejected  || 0, color: COLORS.red    },
    { label: 'Escalated', count: escalated || 0, color: COLORS.orange },
  ].filter(s => s.count > 0);
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', height: 10, borderRadius: 6, overflow: 'hidden', gap: 2, marginBottom: 6 }}>
        {segs.length === 0
          ? <div style={{ flex: 1, background: 'rgba(255,255,255,0.1)', borderRadius: 4 }} />
          : segs.map(s => <div key={s.label} style={{ flex: s.count, background: s.color, borderRadius: 4 }} />)}
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {segs.map(s => (
          <span key={s.label} style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: s.color, display: 'inline-block' }} />
            {s.label} <strong style={{ color: '#fff' }}>{s.count}</strong>
            <span style={{ color: 'rgba(255,255,255,0.4)' }}>({Math.round((s.count / safe) * 100)}%)</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function BarChart({ data, valueKey, labelKey, color, height, title }) {
  const vk = valueKey || 'val'; const lk = labelKey || 'label';
  const h = height || 130; const c = color || COLORS.green;
  const max = Math.max(...data.map(d => d[vk] || 0), 1);
  return (
    <div>
      {title && <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>{title}</div>}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: h, overflowX: 'auto', paddingBottom: 4 }}>
        {data.map((d, i) => {
          const barH = Math.max(((d[vk] || 0) / max) * (h - 32), 2);
          return (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 32, flex: 1 }}>
              <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.6)', marginBottom: 2 }}>{d[vk] || 0}</div>
              <div style={{ width: '100%', height: barH, background: c, borderRadius: '3px 3px 0 0' }} />
              <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', marginTop: 3, textAlign: 'center', overflow: 'hidden', maxWidth: 40 }}>{d[lk]}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function NegligenceBar({ label, value, max, color }) {
  const pct = Math.round((value / Math.max(max, 1)) * 100);
  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
        <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)' }}>{label}</span>
        <span style={{ fontSize: 10, color, fontWeight: 700 }}>{value} ({pct}%)</span>
      </div>
      <div style={{ height: 6, borderRadius: 4, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: pct + '%', background: 'linear-gradient(90deg,' + color + ',' + color + '99)', borderRadius: 4, transition: 'width 0.6s ease' }} />
      </div>
    </div>
  );
}

function AdminPerfCard({ admin }) {
  const [open, setOpen] = useState(false);
  const isUni = admin.admin_type === 'university';
  const bc = isUni ? COLORS.purple : COLORS.blue;
  const total = admin.total || 0;
  const neglPct = total > 0 ? Math.round(((admin.escalated || 0) / total) * 100) : 0;
  const neglColor = neglPct >= 40 ? COLORS.red : neglPct >= 20 ? COLORS.amber : COLORS.green;
  const pendPct  = total > 0 ? Math.round(((admin.pending  || 0) / total) * 100) : 0;
  const resPct   = total > 0 ? Math.round(((admin.resolved || 0) / total) * 100) : 0;
  const verPct   = total > 0 ? Math.round(((admin.verified || 0) / total) * 100) : 0;

  return (
    <div style={{ borderRadius: 12, overflow: 'hidden', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', borderLeft: '3px solid ' + bc, marginBottom: 8 }}>
      <div onClick={() => setOpen(o => !o)} style={{ padding: '14px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}>
        <RateRing rate={admin.resolution_rate} />
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{admin.admin_name}</span>
            <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 8, background: bc + '33', color: bc, fontWeight: 700 }}>{isUni ? 'University' : admin.department}</span>
            <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 8, background: neglColor + '22', color: neglColor, fontWeight: 700, marginLeft: 'auto' }}>{neglPct}% negligence</span>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: COLORS.purple, fontWeight: 600 }}>Total: {total}</span>
            <span style={{ fontSize: 11, color: COLORS.green,  fontWeight: 600 }}>Resolved: {admin.resolved || 0}</span>
            <span style={{ fontSize: 11, color: COLORS.amber,  fontWeight: 600 }}>Pending: {admin.pending || 0}</span>
            <span style={{ fontSize: 11, color: COLORS.blue,   fontWeight: 600 }}>Verified: {admin.verified || 0}</span>
            <span style={{ fontSize: 11, color: COLORS.red,    fontWeight: 600 }}>Escalated: {admin.escalated || 0}</span>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>Avg: {admin.avg_resolution_days}d</span>
          </div>
        </div>
        <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>{open ? '\u25b2' : '\u25bc'}</span>
      </div>
      {open && (
        <div style={{ padding: '0 16px 16px' }}>
          <StatusBar total={total} pending={admin.pending} verified={admin.verified} resolved={admin.resolved} rejected={admin.rejected} escalated={admin.escalated} />
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>Performance Bars</div>
            <NegligenceBar label="Resolution Rate" value={admin.resolved || 0} max={total} color={COLORS.green} />
            <NegligenceBar label="Verification Rate" value={admin.verified || 0} max={total} color={COLORS.blue} />
            <NegligenceBar label="Pending Rate" value={admin.pending || 0} max={total} color={COLORS.amber} />
            <NegligenceBar label="Negligence (Escalated)" value={admin.escalated || 0} max={total} color={neglColor} />
          </div>
        </div>
      )}
    </div>
  );
}

function DeptCard({ dept }) {
  const [open, setOpen] = useState(false);
  const total = dept.total || 0;
  const neglPct = total > 0 ? Math.round(((dept.escalated || 0) / total) * 100) : 0;
  const neglColor = neglPct >= 40 ? COLORS.red : neglPct >= 20 ? COLORS.amber : COLORS.green;
  return (
    <div style={{ borderRadius: 12, overflow: 'hidden', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', marginBottom: 8 }}>
      <div onClick={() => setOpen(o => !o)} style={{ padding: '14px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}>
        <RateRing rate={dept.resolution_rate} />
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{dept.department}</span>
            <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 8, background: neglColor + '22', color: neglColor, fontWeight: 700, marginLeft: 'auto' }}>{neglPct}% negligence</span>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: COLORS.purple, fontWeight: 600 }}>Total: {total}</span>
            <span style={{ fontSize: 11, color: COLORS.green,  fontWeight: 600 }}>Resolved: {dept.resolved || 0}</span>
            <span style={{ fontSize: 11, color: COLORS.amber,  fontWeight: 600 }}>Pending: {dept.pending || 0}</span>
            <span style={{ fontSize: 11, color: COLORS.red,    fontWeight: 600 }}>Rejected: {dept.rejected || 0}</span>
            <span style={{ fontSize: 11, color: COLORS.orange, fontWeight: 600 }}>Escalated: {dept.escalated || 0}</span>
          </div>
        </div>
        <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>{open ? '\u25b2' : '\u25bc'}</span>
      </div>
      {open && (
        <div style={{ padding: '0 16px 16px' }}>
          <StatusBar total={total} pending={dept.pending} verified={dept.verified} resolved={dept.resolved} rejected={dept.rejected} escalated={dept.escalated} />
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>Performance Bars</div>
            <NegligenceBar label="Resolution Rate" value={dept.resolved || 0} max={total} color={COLORS.green} />
            <NegligenceBar label="Pending Rate" value={dept.pending || 0} max={total} color={COLORS.amber} />
            <NegligenceBar label="Negligence (Escalated)" value={dept.escalated || 0} max={total} color={neglColor} />
          </div>
        </div>
      )}
    </div>
  );
}

function ComplaintBlock({ complaints, title, color }) {
  const [open, setOpen] = useState(false);
  if (!complaints || complaints.length === 0) return null;
  return (
    <div style={{ marginBottom: 10 }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '8px 0' }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
        <span style={{ fontSize: 12, fontWeight: 700, color: '#fff' }}>{title}</span>
        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginLeft: 'auto' }}>{complaints.length} {open ? '\u25b2' : '\u25bc'}</span>
      </div>
      {open && (
        <div style={{ paddingLeft: 16 }}>
          {complaints.map((c, i) => (
            <div key={i} style={{ padding: '6px 10px', borderRadius: 8, background: 'rgba(255,255,255,0.04)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 10 }}>
              <StatusBadge status={c.status} />
              <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', flex: 1 }}>{c.title}</span>
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{c.student_name}</span>
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>{c.created_at ? new Date(c.created_at).toLocaleDateString() : ''}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReviewForm({ report, onDone }) {
  const [note, setNote] = useState(report.authority_note || '');
  const [status, setStatus] = useState('reviewed');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const submit = async () => {
    setSaving(true); setErr('');
    try {
      await api.post('/admin-reports/' + report.id + '/review', { status, note });
      onDone();
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to save review');
    } finally { setSaving(false); }
  };
  return (
    <div style={{ marginTop: 14, padding: 14, borderRadius: 10, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.6)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.6 }}>Authority Review</div>
      {err && <div style={{ fontSize: 12, color: COLORS.red, marginBottom: 8 }}>{err}</div>}
      <div style={{ marginBottom: 10 }}>
        <label style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', display: 'block', marginBottom: 4 }}>Status</label>
        <select value={status} onChange={e => setStatus(e.target.value)}
          style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 7, padding: '7px 10px', color: '#fff', fontSize: 12, width: '100%' }}>
          <option value="reviewed">Reviewed</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>
      <div style={{ marginBottom: 10 }}>
        <label style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', display: 'block', marginBottom: 4 }}>Note (optional)</label>
        <textarea value={note} onChange={e => setNote(e.target.value)} rows={3}
          style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 7, padding: '7px 10px', color: '#fff', fontSize: 12, width: '100%', resize: 'vertical' }} />
      </div>
      <button onClick={submit} disabled={saving}
        style={{ padding: '7px 18px', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
        {saving ? 'Saving...' : 'Submit Review'}
      </button>
    </div>
  );
}

function ReportCard({ report, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const snap = (() => { try { return typeof report.complaints_snapshot === 'string' ? JSON.parse(report.complaints_snapshot) : (report.complaints_snapshot || report.complaints_by_status || {}); } catch { return {}; } })();
  const statusColor = { submitted: COLORS.amber, reviewed: COLORS.blue, approved: COLORS.green, rejected: COLORS.red };
  const sc = statusColor[report.status] || COLORS.purple;
  const total = report.total_complaints || 0;
  const neglPct = total > 0 ? Math.round(((report.escalated_complaints || 0) / total) * 100) : 0;
  const neglColor = neglPct >= 40 ? COLORS.red : neglPct >= 20 ? COLORS.amber : COLORS.green;
  return (
    <div style={{ borderRadius: 12, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', marginBottom: 8, overflow: 'hidden' }}>
      <div onClick={() => setOpen(o => !o)} style={{ padding: '14px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{report.admin_name}</span>
            <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 6, background: sc + '22', color: sc, fontWeight: 700, textTransform: 'uppercase' }}>{report.status}</span>
            <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 6, background: neglColor + '22', color: neglColor, fontWeight: 700 }}>{neglPct}% negl.</span>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginLeft: 'auto' }}>{MONTHS[(report.month || 1) - 1]} {report.year}</span>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: COLORS.purple }}>Total: {total}</span>
            <span style={{ fontSize: 11, color: COLORS.green }}>Resolved: {report.resolved_complaints || 0}</span>
            <span style={{ fontSize: 11, color: COLORS.amber }}>Pending: {report.pending_complaints || 0}</span>
            <span style={{ fontSize: 11, color: COLORS.orange }}>Escalated: {report.escalated_complaints || 0}</span>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{report.department || 'University'}</span>
          </div>
        </div>
        <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>{open ? '\u25b2' : '\u25bc'}</span>
      </div>
      {open && (
        <div style={{ padding: '0 16px 16px' }}>
          <StatusBar total={total} pending={report.pending_complaints} verified={report.verified_complaints} resolved={report.resolved_complaints} rejected={report.rejected_complaints} escalated={report.escalated_complaints} />
          <div style={{ marginTop: 10, marginBottom: 12 }}>
            <NegligenceBar label="Resolution Rate" value={report.resolved_complaints || 0} max={total} color={COLORS.green} />
            <NegligenceBar label="Negligence (Escalated)" value={report.escalated_complaints || 0} max={total} color={neglColor} />
          </div>
          {report.summary && <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', marginBottom: 8, lineHeight: 1.6 }}><strong style={{ color: '#fff' }}>Summary:</strong> {report.summary}</div>}
          {report.challenges && <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', marginBottom: 6 }}><strong style={{ color: 'rgba(255,255,255,0.8)' }}>Challenges:</strong> {report.challenges}</div>}
          {report.actions_taken && <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', marginBottom: 6 }}><strong style={{ color: 'rgba(255,255,255,0.8)' }}>Actions:</strong> {report.actions_taken}</div>}
          {report.recommendations && <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', marginBottom: 10 }}><strong style={{ color: 'rgba(255,255,255,0.8)' }}>Recommendations:</strong> {report.recommendations}</div>}
          {Object.entries(snap).map(([st, list]) => list && list.length > 0 && (
            <ComplaintBlock key={st} complaints={list} title={st.charAt(0).toUpperCase() + st.slice(1)}
              color={({ pending: COLORS.amber, verified: COLORS.blue, resolved: COLORS.green, rejected: COLORS.red, escalated: COLORS.orange })[st] || COLORS.purple} />
          ))}
          {report.authority_note && (
            <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 8, background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.25)', fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>
              <strong style={{ color: COLORS.purple }}>Authority Note:</strong> {report.authority_note}
            </div>
          )}
          {!reviewing
            ? <button onClick={() => setReviewing(true)} style={{ marginTop: 12, padding: '6px 16px', background: 'rgba(99,102,241,0.2)', border: '1px solid rgba(99,102,241,0.4)', borderRadius: 8, color: '#a5b4fc', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Review</button>
            : <ReviewForm report={report} onDone={() => { setReviewing(false); onRefresh(); }} />}
        </div>
      )}
    </div>
  );
}

function AnalyticsSection({ analytics, filterMonth, filterYear, setFilterMonth, setFilterYear, onApply, onClear }) {
  const { dept_performance = [], admin_performance = [], university_performance = [], monthly_trend = [], authority_type } = analytics;
  const trendResolved = monthly_trend.map(m => ({ label: m.label, val: m.resolved }));
  const trendTotal    = monthly_trend.map(m => ({ label: m.label, val: m.total }));
  const deptBar    = dept_performance.map(d => ({ label: d.department.length > 8 ? d.department.slice(0, 8) + '...' : d.department, val: d.total }));
  const deptResBar = dept_performance.map(d => ({ label: d.department.length > 8 ? d.department.slice(0, 8) + '...' : d.department, val: d.resolved }));
  const showDept = authority_type !== 'university';
  const showUni  = authority_type !== 'department';

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
        <select value={filterMonth} onChange={e => setFilterMonth(e.target.value)}
          style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, padding: '7px 12px', color: '#fff', fontSize: 12 }}>
          <option value="">All Months</option>
          {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
        </select>
        <select value={filterYear} onChange={e => setFilterYear(e.target.value)}
          style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, padding: '7px 12px', color: '#fff', fontSize: 12 }}>
          <option value="">All Years</option>
          {[2023, 2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <button onClick={onApply} style={{ padding: '7px 16px', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Apply</button>
        <button onClick={() => { setFilterMonth(''); setFilterYear(''); if (onClear) onClear(); }}
          style={{ padding: '7px 14px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, color: '#fff', fontSize: 12, cursor: 'pointer' }}>Clear</button>
      </div>

      <SectionCard title="Monthly Trend" subtitle="Last 12 months - total vs resolved" delay="0.1s" style={{ marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 16 }}>
          <BarChart data={trendTotal}    valueKey="val" labelKey="label" color={COLORS.blue}  title="Total Complaints" />
          <BarChart data={trendResolved} valueKey="val" labelKey="label" color={COLORS.green} title="Resolved" />
        </div>
      </SectionCard>

      {showDept && (
        <SectionCard title="Department Performance" subtitle={authority_type === 'department' ? 'Your department complaints' : 'Resolution rate per department'} delay="0.15s" style={{ marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 16, marginBottom: 16 }}>
            <BarChart data={deptBar}    valueKey="val" labelKey="label" color={COLORS.purple} title="Total by Dept" height={120} />
            <BarChart data={deptResBar} valueKey="val" labelKey="label" color={COLORS.green}  title="Resolved by Dept" height={120} />
          </div>
          {dept_performance.length === 0 ? <EmptyState text="No department data" /> : dept_performance.map((d, i) => <DeptCard key={i} dept={d} />)}
        </SectionCard>
      )}

      {showDept && (
        <SectionCard title="Department Admin Performance" subtitle={authority_type === 'department' ? 'Your department admin stats' : 'Per-admin resolution and negligence'} delay="0.2s" style={{ marginBottom: 16 }}>
          {admin_performance.length === 0 ? <EmptyState text="No department admins found" /> : admin_performance.map((a, i) => <AdminPerfCard key={i} admin={a} />)}
        </SectionCard>
      )}

      {showUni && (
        <SectionCard title="University Admin Performance" subtitle="University-wide complaint handling and negligence" delay="0.25s" style={{ marginBottom: 16 }}>
          {university_performance.length === 0 ? <EmptyState text="No university admins found" /> : university_performance.map((a, i) => <AdminPerfCard key={i} admin={a} />)}
        </SectionCard>
      )}
    </div>
  );
}

function ReportsDashboardInner() {
  const { theme } = useTheme();
  const s = buildStyles(theme);
  const navigate = useNavigate();
  const [tab, setTab] = useState('analytics');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  React.useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [analyticsError, setAnalyticsError] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState('');

  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsError, setReportsError] = useState('');
  const [rptMonth, setRptMonth] = useState('');
  const [rptYear, setRptYear] = useState('');
  const [rptStatus, setRptStatus] = useState('');

  const fetchAnalytics = useCallback(async (m, y) => {
    setAnalyticsLoading(true); setAnalyticsError('');
    try {
      const params = {};
      if (m) params.month = m;
      if (y) params.year  = y;
      const res = await api.get('/admin-reports/analytics', { params });
      setAnalytics(res.data);
    } catch (e) {
      setAnalyticsError(e.response?.data?.error || 'Failed to load analytics');
    } finally { setAnalyticsLoading(false); }
  }, []);

  const fetchReports = useCallback(async () => {
    setReportsLoading(true); setReportsError('');
    try {
      const params = {};
      if (rptMonth)  params.month  = rptMonth;
      if (rptYear)   params.year   = rptYear;
      if (rptStatus) params.status = rptStatus;
      const res = await api.get('/admin-reports/all', { params });
      setReports(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setReportsError(e.response?.data?.error || 'Failed to load reports');
    } finally { setReportsLoading(false); }
  }, [rptMonth, rptYear, rptStatus]);

  useEffect(() => { fetchAnalytics('', ''); }, [fetchAnalytics]);
  useEffect(() => { if (tab === 'reports') fetchReports(); }, [tab, fetchReports]);

  return (
    <div style={s.page}>
      <div style={s.bgGrid} /><div style={s.bgOrb1} /><div style={s.bgOrb2} /><div style={s.bgOrb3} />
      <div style={s.layout}>
        <Sidebar collapsed={!sidebarOpen} onToggle={() => setSidebarOpen(o => !o)}
          mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />
        <div style={{ ...s.main, marginLeft: isMobile ? 0 : (sidebarOpen ? 248 : 0), transition: 'margin-left 0.3s ease' }}>
          <Topbar title="Reports & Analytics" subtitle="Performance overview with negligence tracking"
            onToggleSidebar={() => setSidebarOpen(o => !o)}
            onMobileMenu={() => setMobileOpen(true)}
            actions={
              <button className="ur-btn-hover" style={{ ...s.btnSecondary, fontSize: 12, padding: '6px 12px' }}
                onClick={() => tab === 'analytics' ? fetchAnalytics(filterMonth, filterYear) : fetchReports()}>
                Refresh
              </button>
            }
          />
          <div style={s.content}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
              {[['analytics', 'Performance Analytics'], ['reports', 'Submitted Reports']].map(([key, label]) => (
                <button key={key} onClick={() => setTab(key)} className="ur-btn-hover"
                  style={{ padding: '8px 20px', borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: 'pointer', border: 'none',
                    background: tab === key ? 'linear-gradient(135deg,#6366f1,#8b5cf6)' : 'rgba(255,255,255,0.1)',
                    color: tab === key ? '#fff' : 'rgba(255,255,255,0.7)' }}>
                  {label}
                </button>
              ))}
            </div>

            {tab === 'analytics' && (
              analyticsLoading ? <LoadingScreen /> :
              analyticsError ? <div style={s.alertErr}>{analyticsError}</div> :
              analytics && <AnalyticsSection analytics={analytics} filterMonth={filterMonth} filterYear={filterYear}
                setFilterMonth={setFilterMonth} setFilterYear={setFilterYear}
                onApply={() => fetchAnalytics(filterMonth, filterYear)}
                onClear={() => fetchAnalytics('', '')} />
            )}

            {tab === 'reports' && (
              <div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
                  <select value={rptMonth} onChange={e => setRptMonth(e.target.value)}
                    style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, padding: '7px 12px', color: '#fff', fontSize: 12 }}>
                    <option value="">All Months</option>
                    {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                  </select>
                  <select value={rptYear} onChange={e => setRptYear(e.target.value)}
                    style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, padding: '7px 12px', color: '#fff', fontSize: 12 }}>
                    <option value="">All Years</option>
                    {[2023, 2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                  <select value={rptStatus} onChange={e => setRptStatus(e.target.value)}
                    style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, padding: '7px 12px', color: '#fff', fontSize: 12 }}>
                    <option value="">All Status</option>
                    <option value="submitted">Submitted</option>
                    <option value="reviewed">Reviewed</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                  <button onClick={fetchReports} style={{ padding: '7px 16px', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Filter</button>
                </div>
                {reportsLoading ? <LoadingScreen /> :
                 reportsError ? <div style={s.alertErr}>{reportsError}</div> :
                 !reports.length ? <EmptyState text="No reports found" sub="No admin reports submitted yet" /> :
                 reports.map(r => <ReportCard key={r.id} report={r} onRefresh={fetchReports} />)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ReportsDashboard() {
  return <ThemeProvider><ReportsDashboardInner /></ThemeProvider>;
}
